"use client";

import { useMemo, useState } from "react";
import { useForm, Controller, type FieldValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z, type ZodTypeAny } from "zod";
import type {
  CContactFormContent,
  CContactFormField,
} from "@/lib/api/cms-catalog";
import {
  Field,
  Input,
  PhoneInput,
  Textarea,
  Checkbox,
  FormStatus,
  Honeypot,
  SubmitButton,
} from "@/components/ui/Form";
import { Icon, type IconName } from "@/lib/icons/Icon";
import { toast } from "@/lib/notifications/toast";
import { MapView } from "./MapView";
import { useT } from "@/lib/i18n/context";
import { useAntiSpamToken } from "@/lib/cms/useAntiSpamToken";
import { cDedicatedContactFormTranslations, type CDedicatedContactFormTranslations } from "./CDedicatedContactForm.i18n";
import styles from "./CDedicatedContactForm.module.css";

/**
 * CDedicatedContactForm — visitor view komponentu `c_dedicated_contact_form`.
 *
 * **Data source:** `C_DEDICATED_CONTACT_FORM_CONTROLLER::getApiContent()` zwraca
 * config formularza (pola, labels, anti-spam token, endpoints, hero, contact_info,
 * side_card, dedicated_icons, subject_options).
 *
 * **Walidacja — React Hook Form + Zod (industry standard 2025):**
 *   - Schema budowany DYNAMICZNIE z `content.fields` (admin per okno decyduje
 *     które pola pokazać i które wymagać). Każde pole dostaje base validator
 *     wg typu (`email`/`tel`/`url`/`text`) + opcjonalny `required`.
 *   - Client-side: Zod resolver waliduje przed submitem; błędy renderowane
 *     bezpośrednio pod inputem przez `<Field error={...}>`.
 *   - Server-side: gdy backend zwróci `json.errors` (code 1010), mapujemy
 *     przez `setError(name, {type: 'server'})` — błędy też lądują pod
 *     odpowiednimi polami, NIE w top banner'ze.
 *   - Top banner (`<FormStatus>`) tylko dla błędów systemowych: rate limit,
 *     token wygasł, sieć, min-delay przed submitem.
 *
 * **Anti-spam — 4 warstwy bez external integration:**
 *   1. HMAC token (`antispam.token` + `antispam.timestamp`) — backend signed,
 *      frontend odsyła bez zmian. Bot nie podrobi (nie zna `cfg()->encKey`).
 *   2. Min `antispam.min_delay_seconds` od mountu — frontend mierzy lokalnie
 *      (NIE od server timestamp — clock skew dawał ujemne age).
 *   3. Honeypot (`antispam.honeypot_field`) — komponent `<Honeypot>` poza RHF.
 *   4. Rate limit per IP — backend (transparentnie); UI pokazuje banner gdy 429.
 *
 * **UI primitives:** `@/components/ui/Form` (Field/Input/Textarea/Checkbox/
 * FormStatus/Honeypot) — brand tokens, focus rings, error state, a11y.
 */
export function CDedicatedContactForm({ content }: CDedicatedContactFormProps) {
  /* Page title (h1) i card wrapper renderuje kontener `CDefault` z
     `components/cms/registry/variants.tsx`. Ten komponent renderuje TYLKO
     zawartość formularza (intro + sekcyjny h2 + form + map). */
  const t = useT(cDedicatedContactFormTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <FormStatus variant="error">{t.noConfig}</FormStatus>
      </div>
    );
  }

  if (content.config.send_to === "1" && !content.config.crm_available) {
    return (
      <div className={styles.root}>
        <FormStatus variant="warning">
          {t.crmModeWithoutModule.body1}
          <strong> {t.crmModeWithoutModule.moduleName}</strong>{" "}
          {t.crmModeWithoutModule.body2}
        </FormStatus>
      </div>
    );
  }

  if (content.config.send_to !== "1" && content.config.send_to !== "2") {
    return (
      <div className={styles.root}>
        <FormStatus variant="error">{t.unknownSendMode(content.config.send_to)}</FormStatus>
      </div>
    );
  }

  return <ContactFormView content={content} t={t} />;
}

export type CDedicatedContactFormProps = {
  content: CContactFormContent;
};

/* ─── Schema builder ────────────────────────────────────────────────────── */

/**
 * Buduje Zod schema DYNAMICZNIE z `content.fields` (admin config) +
 * `text` (zawsze wymagane) + opcjonalny `personal_data` (RODO).
 *
 * Walidacja per typ (zgodna z backendem `IAPI_send::validateFieldType`):
 *   - `email` -> format email
 *   - `tel` -> regex telefonu `^[\d +()\-]{6,20}$`
 *   - `url` -> musi zaczynać się od `http://` lub `https://`
 *   - `company_nip` -> dokładnie 10 cyfr (po usunięciu separatorów)
 *
 * Required vs opcjonalne:
 *   - `required=true` -> `.min(1, "Pole 'X' jest wymagane.")` przed type
 *     validator; pusty string -> message.
 *   - `required=false` -> walidator typu działa TYLKO gdy pole niepuste;
 *     pusty string przepuszczamy przez `.or(z.literal(""))`.
 */
function buildSchema(
  fields: CContactFormField[],
  hasRodoCheckbox: boolean,
  hasSubjectOptions: boolean,
  t: CDedicatedContactFormTranslations,
) {
  const shape: Record<string, ZodTypeAny> = {};

  /* Subject dropdown (opcjonalne - render tylko gdy backend zwrocil pozycje ze
     slownika `c_contact_form_subjects`). Pusta wartosc = user nie wybral tematu,
     przepuszczamy bo dropdown nie jest wymagany. */
  if (hasSubjectOptions) {
    shape.subject_selected = z.string();
  }

  for (const f of fields) {
    const requiredMsg = t.validation.requiredField(f.label);
    let typeValidator: z.ZodString = z.string();

    /* Type-specific validators — message wzorowany na backendzie żeby user
       widział spójne komunikaty bez round-tripa. */
    if (f.type === "email") {
      typeValidator = typeValidator.email(t.validation.invalidEmail);
    } else if (f.type === "url") {
      typeValidator = typeValidator.regex(/^https?:\/\//i, t.validation.invalidUrl);
    } else if (f.type === "tel") {
      typeValidator = typeValidator.regex(/^[\d +()\-]{6,20}$/, t.validation.invalidPhone);
    }
    if (f.name === "company_nip") {
      /* NIP: po usunięciu separatorów musi być dokładnie 10 cyfr (proste
         length check; checksum walidacja backendowo w przyszłości). */
      typeValidator = typeValidator.refine(
        (val) => /^\d{10}$/.test(val.replace(/[^\d]/g, "")),
        t.validation.invalidNip,
      );
    }

    if (f.required) {
      /* Pole wymagane: pusty string -> required message; niepusty -> type validator. */
      shape[f.name] = z.string().min(1, requiredMsg).pipe(typeValidator);
    } else {
      /* Opcjonalne: pusty string OK, niepusty musi pass'ować type validator. */
      shape[f.name] = z.union([typeValidator, z.literal("")]);
    }
  }

  /* Treść wiadomości — zawsze wymagana niezależnie od config. */
  shape.text = z.string().min(1, t.validation.requiredText);

  /* RODO checkbox — wymagany TYLKO gdy admin włączył `show_right_checkbox`.
     `z.literal(true)` wymusza że value MUSI być true (zaznaczone). */
  if (hasRodoCheckbox) {
    shape.personal_data = z.literal(true, {
      message: t.validation.requiredPersonalData,
    });
  } else {
    shape.personal_data = z.boolean().optional();
  }

  return z.object(shape);
}

/* ─── Form view ────────────────────────────────────────────────────────── */

/**
 * Form values shape — dynamic bo lista pól zależy od `content.fields` (config
 * admin'a). RHF generic `FieldValues = Record<string, any>` — używamy bezpośrednio
 * (nie wymyślamy explicit typu, bo schema jest dynamiczna). Walidacja typów
 * spada na Zod schema (runtime).
 */
type FormValues = FieldValues;

function ContactFormView({ content, t }: { content: CContactFormContent; t: CDedicatedContactFormTranslations }) {
  const allFields = useMemo(
    () => [...content.fields.personal, ...content.fields.company],
    [content.fields.personal, content.fields.company],
  );

  const hasSubjectOptions = content.subject_options.length > 0;

  const schema = useMemo(
    () => buildSchema(allFields, content.config.show_right_checkbox, hasSubjectOptions, t),
    [allFields, content.config.show_right_checkbox, hasSubjectOptions, t],
  );

  const defaultValues = useMemo<FormValues>(() => {
    const dv: FormValues = { text: "" };
    for (const f of allFields) dv[f.name] = "";
    if (content.config.show_right_checkbox) dv.personal_data = false;
    if (hasSubjectOptions) dv.subject_selected = "";
    return dv;
  }, [allFields, content.config.show_right_checkbox, hasSubjectOptions]);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    /* `mode: "onTouched"` — walidacja per pole startuje po pierwszym blur'rze,
       potem live na każdym onChange. Daje natychmiastowy feedback bez
       agresywnego pokazywania błędów zanim user wpisał cokolwiek. */
    mode: "onTouched",
  });

  const [topError, setTopError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");

  /* Lokalna referencja czasu mountu — używana do min-delay check niezależnie
     od server timestamp (clock skew immunity). */
  const [mountedAt] = useState(() => Date.now());

  /* Fresh anti-spam token per mount - bypass Next.js SWR cache SSR'a. Scope
     musi pasowac 1:1 do backend'owego `verifyToken` w `IAPI_send`. */
  const antispam = useAntiSpamToken([content.id]);

  const onSubmit = async (values: FormValues) => {
    setTopError(null);

    const ageSec = Math.floor((Date.now() - mountedAt) / 1000);
    if (ageSec < content.antispam.min_delay_seconds) {
      setTopError(t.status.waitBeforeSubmit(content.antispam.min_delay_seconds - ageSec));
      return;
    }

    if (!antispam.token || !antispam.timestamp) {
      setTopError(antispam.error ?? t.status.sendFailed);
      return;
    }

    /* Buduj payload: pola formularza pod `field`, treść + checkbox top-level. */
    const fieldValues: Record<string, string> = {};
    for (const f of allFields) {
      const v = values[f.name];
      fieldValues[f.name] = typeof v === "string" ? v : "";
    }

    const body: Record<string, unknown> = {
      id_catalog: content.id,
      token: antispam.token,
      timestamp: antispam.timestamp,
      [content.antispam.honeypot_field]: honeypot,
      field: fieldValues,
      text: typeof values.text === "string" ? values.text : "",
      personal_data: values.personal_data === true,
    };
    if (hasSubjectOptions && typeof values.subject_selected === "string") {
      body.subject_selected = values.subject_selected;
    }

    try {
      const res = await fetch(`/api/c-dedicated-contact-form/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        error?: string;
        code?: number;
        errors?: Record<string, string>;
      };

      if (!res.ok || !json.ok) {
        /* Server-side errors mapowane do per-pole errors przez RHF setError.
           `type: 'server'` (vs 'validation') żeby ewentualne future logic
           mogła rozróżnić źródło. Pola spoza schemy (np. backend doda nowy
           field) ignorujemy bez crashowania. */
        if (
          json.errors &&
          typeof json.errors === "object" &&
          Object.keys(json.errors).length > 0
        ) {
          for (const [name, msg] of Object.entries(json.errors)) {
            if (typeof msg !== "string") continue;
            setError(name, {
              type: "server",
              message: msg,
            });
          }
        } else {
          setTopError(
            json.message ||
              (res.status === 429 ? t.status.rateLimit : t.status.sendFailed),
          );
        }
        return;
      }

      /* Standard komunikatów (zob. `lib/notifications/toast.ts`):
         success po routine action -> `toast.success` (transient, top-right,
         auto-dismiss 4s). NIE zastępujemy formularza success bannerem —
         formularz zostaje widoczny (z value zresetowanym), user widzi
         potwierdzenie nie tracąc kontekstu. */
      toast.success(
        json.message ||
          content.lang.messages?.send_ok ||
          t.status.sendSuccess,
      );
      reset(defaultValues);
      setHoneypot("");
    } catch (err) {
      setTopError(
        err instanceof Error
          ? t.status.networkError(err.message)
          : t.status.networkErrorGeneric,
      );
    }
  };

  return (
    <div className={styles.root}>
      {/* Modern header - eyebrow (mala etykieta typu KONTAKT), tytul, opis.
          Kazde pole opcjonalne - null-check per pole; caly blok znika gdy hero=null. */}
      {content.hero && (
        <header className={styles.hero}>
          <div className={styles.heroInner}>
            {content.hero.eyebrow && (
              <div className={styles.heroEyebrow}>{content.hero.eyebrow}</div>
            )}
            {content.hero.title && (
              <h1 className={styles.heroTitle}>{content.hero.title}</h1>
            )}
            {content.hero.description && (
              <p className={styles.heroDescription}>{content.hero.description}</p>
            )}
          </div>
        </header>
      )}

      {/* 2-kolumnowy layout: lewa = bloki kontaktowe + notka boczna, prawa = formularz.
          Gdy brak contact_info I brak side_card -> layout collapse do 1 kolumny
          (sam formularz) przez CSS grid-template-columns. */}
      <div className={styles.layout}>
        {(content.contact_info || content.side_card) && (
          <aside className={styles.infoCol}>
            {content.contact_info && (
              <div className={styles.contactBlocks}>
                {content.contact_info.email && (
                  <div className={styles.contactBlock}>
                    {/* Ikona Nucleo Outline - admin picker (IFORM addFontIcon type=nucleo-o).
                        Fallback `ui-1_email-84` gdy admin nie wybral. Global CSS z app/nucleo-outline.css
                        renderuje przez ::before content: "\eXXX" - zero external HTTP, glyph z woff2. */}
                    <i
                      className={`${styles.contactBlockIcon} nc-icon-outline ${content.dedicated_icons?.email ?? "ui-1_email-84"}`}
                      aria-hidden="true"
                    />
                    <div className={styles.contactBlockBody}>
                      <div className={styles.contactBlockLabel}>E-mail</div>
                      <a className={styles.contactBlockValue} href={`mailto:${content.contact_info.email}`}>
                        {content.contact_info.email}
                      </a>
                    </div>
                  </div>
                )}
                {content.contact_info.phone && (
                  <div className={styles.contactBlock}>
                    <i
                      className={`${styles.contactBlockIcon} nc-icon-outline ${content.dedicated_icons?.phone ?? "ui-3_phone"}`}
                      aria-hidden="true"
                    />
                    <div className={styles.contactBlockBody}>
                      <div className={styles.contactBlockLabel}>Telefon</div>
                      <a className={styles.contactBlockValue} href={`tel:${content.contact_info.phone.replace(/\s+/g, "")}`}>
                        {content.contact_info.phone}
                      </a>
                    </div>
                  </div>
                )}
                {content.contact_info.addresses.map((addr, i) => {
                  /* Adresy mapowane po indexie: [0] = address_1, [1] = address_2 (bo backend
                     zwraca dokladnie te dwa sloty w tej kolejnosci). */
                  const iconKey = i === 0 ? "address_1" : "address_2";
                  const iconName = content.dedicated_icons?.[iconKey] ?? "location_map-pin";
                  return (
                    <div className={styles.contactBlock} key={`addr-${i}`}>
                      <i
                        className={`${styles.contactBlockIcon} nc-icon-outline ${iconName}`}
                        aria-hidden="true"
                      />
                      <div className={styles.contactBlockBody}>
                        <div className={styles.contactBlockLabel}>{addr.title}</div>
                        <div className={styles.contactBlockValue} style={{ whiteSpace: "pre-line" }}>
                          {addr.body}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {content.side_card && (
              <div className={styles.sideCard}>
                <div className={styles.sideCardTitle}>{content.side_card.title}</div>
                {content.side_card.body && (
                  <div className={styles.sideCardBody} style={{ whiteSpace: "pre-line" }}>
                    {content.side_card.body}
                  </div>
                )}
              </div>
            )}
          </aside>
        )}

        <div className={styles.formCol}>
      {content.config.intro_html && (
        <div
          className={styles.intro}
          dangerouslySetInnerHTML={{ __html: content.config.intro_html }}
        />
      )}

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        {content.fields.personal.length > 0 && (
          <section className={styles.fieldsSection}>
            <h3 className={styles.sectionHeader}>{t.sectionHeaders.personal}</h3>
            <div className={styles.fieldsGrid}>
              <FieldGroup
                fields={content.fields.personal}
                register={register}
                control={control}
                errors={errors as Record<string, { message?: string }>}
                disabled={isSubmitting}
              />
            </div>
          </section>
        )}
        {content.fields.company.length > 0 && (
          <section className={styles.fieldsSection}>
            <h3 className={styles.sectionHeader}>{t.sectionHeaders.company}</h3>
            <div className={styles.fieldsGrid}>
              <FieldGroup
                fields={content.fields.company}
                register={register}
                control={control}
                errors={errors as Record<string, { message?: string }>}
                disabled={isSubmitting}
              />
            </div>
          </section>
        )}

        {hasSubjectOptions && (
          <Field
            label={content.lang.labels?.subject || t.labels.subject}
            error={
              typeof errors.subject_selected?.message === "string"
                ? errors.subject_selected.message
                : undefined
            }
          >
            {/* Dropdown "Czego dotyczy zapytanie" - pozycje ze slownika
                m_dictionary (`c_contact_form_subjects`). Frontend wysyla wybrana
                wartosc pod kluczem `subject_selected` w POST (rozne od honeypot
                `subject`). Pusty select = "-- wybierz --" jako pierwsza opcja
                bez value, RHF zapisuje jako "" (nie NULL). Placeholder z i18n. */}
            <select
              className={styles.subjectSelect}
              disabled={isSubmitting}
              {...register("subject_selected")}
            >
              <option value="">{t.labels.subjectPlaceholder}</option>
              {content.subject_options.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field
          label={content.lang.labels?.text || t.labels.text}
          required
          error={typeof errors.text?.message === "string" ? errors.text.message : undefined}
        >
          <Textarea
            rows={6}
            disabled={isSubmitting}
            {...register("text")}
          />
        </Field>

        {content.config.show_right_checkbox && (
          <div className={styles.fieldRow}>
            <Controller
              name="personal_data"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value === true}
                  onChange={(e) => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  disabled={isSubmitting}
                  aria-invalid={!!errors.personal_data || undefined}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: content.config.processing_personal_data,
                    }}
                  />
                </Checkbox>
              )}
            />
            {typeof errors.personal_data?.message === "string" && (
              <div className={styles.fieldError}>
                {errors.personal_data.message}
              </div>
            )}
          </div>
        )}

        {/* Honeypot poza RHF — uncontrolled stan w `useState`. RHF nie wie o
            tym polu (nie ma go w schemie). Boty fillują, ludzie nie. */}
        <Honeypot
          name={content.antispam.honeypot_field}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />

        {topError && <FormStatus variant="error">{topError}</FormStatus>}

        <div className={styles.actions}>
          <SubmitButton
            loading={isSubmitting || !antispam.ready}
            loadingLabel={t.sending}
            disabled={antispam.error != null}
          >
            {content.lang.send || t.send}
          </SubmitButton>
        </div>
      </form>
        </div>
      </div>

      {/* Mapa pod formularzem — renderowana gdy admin włączył `show_map`
          w panelu Imperial. Dispatcher per API (Google/OSM) wewnątrz MapView. */}
      {content.config.map && content.config.map.enabled && (
        <div className={styles.mapWrap}>
          <MapView map={content.config.map} />
        </div>
      )}
    </div>
  );
}

/* ─── Field group ──────────────────────────────────────────────────────── */

function FieldGroup({
  fields,
  register,
  control,
  errors,
  disabled,
}: {
  fields: CContactFormField[];
  register: ReturnType<typeof useForm>["register"];
  control: ReturnType<typeof useForm>["control"];
  errors: Record<string, { message?: string } | undefined>;
  disabled: boolean;
}) {
  return (
    <>
      {fields.map((f) => {
        const err = errors[f.name];
        const errMessage = typeof err?.message === "string" ? err.message : undefined;
        const iconName = iconFor(f.name, f.type);

        /* Phone — wzorzec z CUserAccounts `ProfileTabPersonal`: PhoneInput
           jest custom controlled (country selector + format auto). RHF
           wymaga `<Controller>` bo nie pasuje standardowy `register` ref
           (PhoneInput ma własny ref pattern do component instance, nie
           HTMLInputElement). */
        if (f.type === "tel") {
          return (
            <Field
              key={f.name}
              label={f.label}
              required={f.required}
              error={errMessage}
            >
              <Controller
                name={f.name}
                control={control}
                render={({ field: rhfField }) => (
                  <PhoneInput
                    value={(rhfField.value as string) ?? ""}
                    onChange={(v) => rhfField.onChange(v ?? "")}
                    disabled={disabled}
                  />
                )}
              />
            </Field>
          );
        }

        return (
          <Field
            key={f.name}
            label={f.label}
            required={f.required}
            error={errMessage}
          >
            <Input
              type={f.type}
              autoComplete={autocompleteFor(f.name, f.type)}
              disabled={disabled}
              prefix={iconName ? <Icon name={iconName} /> : undefined}
              {...register(f.name)}
            />
          </Field>
        );
      })}
    </>
  );
}

/**
 * Mapping nazwy/typu pola -> ikona Phosphor wyświetlana jako prefix w Input.
 * Wzorzec z CUserAccounts (`ProfileTabPersonal` linia 675) — `<Input prefix>`
 * pokazuje ikonę po lewej stronie pola (np. envelope dla email, link dla URL).
 *
 * Floating label z prefix jest obsługiwany przez `Form.module.css`:
 * `:has(.addonPrefix)` ustawia `--label-offset: 48px` żeby label nie
 * nakładał się na ikonę.
 *
 * Zwraca `null` gdy pole nie ma dedykowanej ikony — Input renderuje się
 * bez prefix slot'a (label floating w natywnej pozycji).
 */
function iconFor(name: string, type: string): IconName | null {
  if (type === "email") return "envelope";
  if (type === "tel") return "phone";
  if (type === "url") return "link";
  switch (name) {
    case "name":
    case "surname":
      return "user";
    case "company_name":
      return "buildings";
    default:
      return null;
  }
}

/**
 * Mapping nazwy pola -> HTML5 `autocomplete` attribute. Pozwala browser'owi
 * autofillować pola znanymi danymi (imię, telefon, email) — UX boost.
 */
function autocompleteFor(name: string, type: string): string {
  if (type === "email") return "email";
  if (type === "tel") return "tel";
  if (type === "url") return "url";
  switch (name) {
    case "name":
      return "given-name";
    case "surname":
      return "family-name";
    case "city":
    case "company_city":
      return "address-level2";
    case "state":
    case "company_state":
      return "address-level1";
    case "company_name":
      return "organization";
    default:
      return "off";
  }
}
